import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminCommonAreasService, AdminBuilding, AdminBox, AdminDevice } from '@/services/adminCommonAreasService';
import { adminCommonAreasAttributesService } from '@/services/adminCommonAreasAttributesService';
import { attributeService } from '@/services/attributeService';
import { Attribute } from '@/types';

interface AddCommonAreaAttributeModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  onAdded: () => void;
}

export function AddCommonAreaAttributeModal({
  isOpen,
  onClose,
  projectId,
  onAdded,
}: AddCommonAreaAttributeModalProps) {
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | ''>('');
  const [selectedBoxId, setSelectedBoxId] = useState<number | ''>('');
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | ''>('');
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<Set<number>>(new Set());

  const buildingRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const deviceRef = useRef<HTMLDivElement>(null);
  const attributeRef = useRef<HTMLDivElement>(null);

  // Fetch buildings for the project
  const { data: buildings = [] } = useQuery({
    queryKey: ['admin-common-areas', 'buildings', projectId],
    queryFn: () => adminCommonAreasService.getBuildings(),
    enabled: isOpen,
  });

  const scopedBuildings = buildings.filter((b) => b.project?.id === projectId);

  // Fetch boxes for selected building
  const { data: boxes = [] } = useQuery({
    queryKey: ['admin-common-areas', 'building', selectedBuildingId, 'boxes'],
    queryFn: () => adminCommonAreasService.getBoxesForBuilding(selectedBuildingId as number),
    enabled: isOpen && selectedBuildingId !== '',
  });

  // Fetch devices for selected box
  const { data: devices = [] } = useQuery({
    queryKey: ['admin-common-areas', 'box', selectedBoxId, 'devices'],
    queryFn: () => adminCommonAreasService.getDevicesForBox(selectedBoxId as number),
    enabled: isOpen && selectedBoxId !== '',
  });

  // Fetch attributes for selected device
  const { data: attributes = [] } = useQuery({
    queryKey: ['attributes', 'device', selectedDeviceId],
    queryFn: () => attributeService.getAttributesByDevice(selectedDeviceId as number),
    enabled: isOpen && selectedDeviceId !== '',
  });

  // Fetch already configured attributes for the project
  const { data: configuredAttributes = [] } = useQuery({
    queryKey: ['admin-common-areas-attributes', projectId],
    queryFn: () => adminCommonAreasAttributesService.getAttributes(projectId),
    enabled: isOpen,
  });

  // Create a set of configured attribute IDs for quick lookup
  const configuredAttributeIds = useMemo(() => {
    return new Set(configuredAttributes.map((attr) => attr.id));
  }, [configuredAttributes]);

  // Pre-select already configured attributes when device changes
  useEffect(() => {
    if (selectedDeviceId !== '' && attributes.length > 0) {
      const alreadyConfigured = attributes
        .filter((attr) => configuredAttributeIds.has(attr.id))
        .map((attr) => attr.id);
      if (alreadyConfigured.length > 0) {
        setSelectedAttributeIds((prev) => {
          const next = new Set(prev);
          alreadyConfigured.forEach((id) => next.add(id));
          return next;
        });
      }
    }
  }, [selectedDeviceId, attributes, configuredAttributeIds]);

  // Reset selections when parent changes
  useEffect(() => {
    if (selectedBuildingId === '') {
      setSelectedBoxId('');
      setSelectedDeviceId('');
      setSelectedAttributeIds(new Set());
    }
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedBoxId === '') {
      setSelectedDeviceId('');
      setSelectedAttributeIds(new Set());
    }
  }, [selectedBoxId]);

  useEffect(() => {
    if (selectedDeviceId === '') {
      setSelectedAttributeIds(new Set());
    }
  }, [selectedDeviceId]);

  const handleClose = () => {
    setSelectedBuildingId('');
    setSelectedBoxId('');
    setSelectedDeviceId('');
    setSelectedAttributeIds(new Set());
    onClose();
  };

  const handleToggleAttribute = (attributeId: number) => {
    setSelectedAttributeIds((prev) => {
      const next = new Set(prev);
      if (next.has(attributeId)) {
        next.delete(attributeId);
      } else {
        next.add(attributeId);
      }
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedAttributeIds.size === 0) return;

    try {
      // Filter out already configured attributes to avoid duplicates
      const newAttributeIds = Array.from(selectedAttributeIds).filter(
        (id) => !configuredAttributeIds.has(id)
      );

      if (newAttributeIds.length === 0) {
        // All selected attributes are already configured
        handleClose();
        return;
      }

      await Promise.all(
        newAttributeIds.map((attrId) =>
          adminCommonAreasAttributesService.addAttribute(projectId, attrId)
        )
      );
      onAdded();
      handleClose();
    } catch (error) {
      console.error('Error adding attributes:', error);
      alert('Erreur lors de l\'ajout des attributes');
    }
  };

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div
        className="relative w-full max-w-4xl mx-4 bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="material-icons-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '20px' }}>
              add
            </span>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Ajouter des attributes</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-[#1E293B] dark:hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <span className="material-icons-outlined" style={{ fontSize: '20px' }}>close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Building Selection */}
          <div ref={buildingRef}>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
              Bâtiment <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedBuildingId === '' ? '' : String(selectedBuildingId)}
              onChange={(e) => setSelectedBuildingId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">— Sélectionner un bâtiment —</option>
              {scopedBuildings.map((b: AdminBuilding) => (
                <option key={b.id} value={b.id}>
                  {b.name ?? b.code ?? `Bâtiment #${b.id}`}
                </option>
              ))}
            </select>
          </div>

          {/* Box Selection */}
          {selectedBuildingId !== '' && (
            <div ref={boxRef}>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                Box <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedBoxId === '' ? '' : String(selectedBoxId)}
                onChange={(e) => setSelectedBoxId(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">— Sélectionner une box —</option>
                {boxes.map((b: AdminBox) => (
                  <option key={b.id} value={b.id}>
                    {b.name ?? b.code ?? `Box #${b.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Device Selection */}
          {selectedBoxId !== '' && (
            <div ref={deviceRef}>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                Device <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedDeviceId === '' ? '' : String(selectedDeviceId)}
                onChange={(e) => setSelectedDeviceId(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">— Sélectionner un device —</option>
                {devices.map((d: AdminDevice) => (
                  <option key={d.id} value={d.id}>
                    {d.name ?? d.code ?? `Device #${d.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Attributes Selection */}
          {selectedDeviceId !== '' && attributes.length > 0 && (
            <div ref={attributeRef}>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                Attributes <span className="text-red-500">*</span>
              </label>
              <div className="border border-slate-200 dark:border-[#1E293B] rounded-lg max-h-64 overflow-y-auto">
                {attributes.map((attr: Attribute) => {
                  const isSelected = selectedAttributeIds.has(attr.id);
                  const isAlreadyConfigured = configuredAttributeIds.has(attr.id);
                  const attrType = typeof attr.type === 'object' && attr.type ? attr.type : null;
                  const hasValue = attr.currentValue !== null && attr.currentValue !== undefined && attr.currentValue !== '';
                  return (
                    <button
                      key={attr.id}
                      type="button"
                      onClick={() => handleToggleAttribute(attr.id)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-200 dark:border-[#1E293B] last:border-b-0 hover:bg-slate-50 dark:hover:bg-[#1E293B] transition-colors ${
                        isSelected ? 'bg-primary/10 dark:bg-primary/20' : ''
                      } ${isAlreadyConfigured && !isSelected ? 'bg-emerald-50 dark:bg-emerald-900/10' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-slate-900 dark:text-white">{attr.name}</div>
                            {isAlreadyConfigured && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                                Déjà configuré
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            {attrType && (
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                {attrType.name ?? attrType.code}
                              </div>
                            )}
                            {hasValue && (
                              <>
                                <span className="text-xs text-slate-400 dark:text-slate-500">•</span>
                                <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                  {attr.currentValue}
                                  {attrType?.unit && <span className="ml-1">{attrType.unit}</span>}
                                </div>
                                {attr.currentValueTs && (
                                  <>
                                    <span className="text-xs text-slate-400 dark:text-slate-500">•</span>
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                      {formatDateTime(attr.currentValueTs)}
                                    </div>
                                  </>
                                )}
                              </>
                            )}
                            {!hasValue && (
                              <div className="text-xs text-slate-400 dark:text-slate-500 italic">
                                Aucune valeur
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          {isSelected ? (
                            <span className="material-icons-outlined text-primary" style={{ fontSize: '24px' }}>
                              check_circle
                            </span>
                          ) : (
                            <span className="material-icons-outlined text-slate-400" style={{ fontSize: '24px' }}>
                              radio_button_unchecked
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {selectedDeviceId !== '' && attributes.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
              Aucun attribute trouvé pour ce device.
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 dark:border-[#1E293B] flex items-center justify-end gap-3 flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-[#1E293B] transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleAdd}
            disabled={selectedAttributeIds.size === 0}
            className={`px-4 py-2 rounded-lg text-white transition-colors ${
              selectedAttributeIds.size > 0
                ? 'bg-primary hover:bg-primary/90'
                : 'bg-slate-400 cursor-not-allowed'
            }`}
          >
            Ajouter (
            {Array.from(selectedAttributeIds).filter((id) => !configuredAttributeIds.has(id)).length})
          </button>
        </div>
      </div>
    </div>
  );
}
